import React, {useEffect} from 'react';

import {
    Clear as CloseIcon,
} from '@mui/icons-material';
import {ButtonBase, Paper, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';

import {useAction} from 'canvas/canvas-actions';
import {useCanvasClear, useCanvasLoad} from 'canvas/useCanvas';
import {useCanvasStore} from 'canvas/zustand';
import {ConfirmDialog, useConfirmDialog} from 'components';
import {useTranslation} from 'hooks';
import {useGroupLoad} from 'module/group/useGroup';
import {useSheet, useSheetDelete, useSheetLoad} from 'module/sheet/useSheet';
import {useUserUtils} from 'module/user/useUser';
import {useLayoutStore, useSnackbarStore} from 'store';

import InfobarIcon from './Icon';

const styles = {
    infoContainer: {
        height: '100%',
        backgroundColor: 'black.light',
        borderRadius: 0,
    },
    headerContainer: {
        borderBottom: '2px solid',
        borderColor: '#5f5f5f'
    },
    headerText: {
        fontSize: '18px',
        color: '#dddddd',
    },
    menueCloseIcon: {
        color: 'red.dark'
    }
};

const Options = ({onComplete}) => {
    const translate = useTranslation();

    const {dispatch} = useAction();

    const setShowFieldSelector = useLayoutStore((state) => state.setShowFieldSelector);

    const {frameIndex, selectedSheet: sheet} = useSheet();
    console.log('SHEET', frameIndex, sheet);

    const {clear: clearCanvas} = useCanvasClear();
    const {load: loadCanvas} = useCanvasLoad();

    const {loading: loading1, load: loadSheets} = useSheetLoad();
    const {loading: loading2, load: loadGroups} = useGroupLoad();
    const {del: deleteSheet, loading: loading3} = useSheetDelete();

    const {isAuthenticated} = useUserUtils();

    const showInfo = useSnackbarStore((state) => state.show);

    const setShowLoading = useLayoutStore((state) => state.setShowLoading);

    const clear = async () => {
        await clearCanvas(sheet);
        onComplete();
    };

    const stepBack = async () => {
        dispatch('previous');
        onComplete();
    };

    const stepNext = async () => {
        dispatch('redo');
        onComplete();
    };

    const undo = async () => {
        await loadCanvas(sheet, isMobile);
        dispatch('push');
        onComplete();
    };

    const reload = async () => {
        if (!isAuthenticated) {
            return;
        }

        try {
            await loadSheets(false);
            await loadGroups(false);
        } catch (e) {
            console.log(e.cause);
            showInfo(e.message, {severity: 'warning'});
        }

        onComplete();
    };

    const selectBackground = () => {
        setShowFieldSelector(true);
        onComplete();
    };

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
    const canvasStatesIndex = useCanvasStore((state) => state.canvasStatesIndex);
    const canvasStates = useCanvasStore((state) => state.canvasStates);

    const backDisabled = canvasStatesIndex <= 0;
    const forwardDisabled = (canvasStatesIndex + 1 >= canvasStates.length);

    const deleteConfirm = useConfirmDialog(
        async () => {
            try {
                await deleteSheet(sheet);
                showInfo('board.sheet.action.delete_success');
                onComplete();
            } catch (e) {
                showInfo(e.message, {severity: 'warning'});
            }
        },
        undefined,
        'board.sheet.action.confirm_delete'
    );

    useEffect(() => {
        setShowLoading(loading1 || loading2 || loading3);

        return () => {
            setShowLoading(false);
        };
    }, [loading1, loading2, loading3, setShowLoading]);

    return <Paper sx={styles.infoContainer}>
        <ConfirmDialog {...deleteConfirm} />
        <Stack direction="row" justifyContent="space-between" p={2} sx={styles.headerContainer}>
            <Typography sx={styles.headerText}>{translate('board.layout.sheet_actions')}</Typography>
            <ButtonBase onClick={onComplete}>
                <CloseIcon sx={styles.menueCloseIcon}/>
            </ButtonBase>
        </Stack>
        <Stack pt={1}>

            {/* preview button */}
            {isMobile && <InfobarIcon
                icon={'Back'}
                disabled={backDisabled}
                text={translate('ui.actions.back')}
                onClick={() => stepBack()}
                color={canvasStatesIndex > 0 ? 'white.main' : 'grey.light'} />
            }

            {/* next button */}
            {isMobile && <InfobarIcon
                icon={'Forward'}
                disabled={forwardDisabled}
                text={translate('ui.actions.next')}
                onClick={() => stepNext()}
                color={(canvasStatesIndex + 1 < canvasStates.length) ? 'white.main' : 'grey.light'}
            /> }

            {isAuthenticated && <InfobarIcon
                text={translate('board.layout.reload')}
                icon={'Reload'}
                onClick={reload}
            /> }

            {/* <InfobarIcon
                text={translate('board.sheet.copy')} icon={'CopySlide'}
                onClick={copy}/> */}

            <InfobarIcon
                disabled={frameIndex > 0}
                text={translate('board.sheet.clear')}
                icon={'DeleteSlide'}
                onClick={clear}
            />

            <InfobarIcon
                disabled={frameIndex > 0}
                text={translate('board.sheet.reset')}
                icon={'FirstPage'}
                onClick={undo}
            />

            <InfobarIcon
                disabled={frameIndex > 0}
                text={translate('board.sheet.select_background')}
                icon={'SlideBackground'}
                onClick={selectBackground}
            />

            <InfobarIcon
                disabled={frameIndex > 0}
                text={translate('board.sheet.action.delete')}
                icon={'DeleteSlide'}
                fill="#c62828"
                onClick={() => deleteConfirm.show()}
            />

            {/* <InfobarIcon
                text={sheet?.frames?.length > 0 ? translate('board.sheet.delete_moves') : translate('board.sheet.activate_moves')}
                icon={'Timeline'}
                onClick={toggleTimeline}/> */}

        </Stack>
    </Paper>;
};

export default Options;
